import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { notificationsAPI } from '../services/api';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);

const POLL_INTERVAL_MS = 15000; // 15 seconds

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const isAuthenticated = !!user;
  // workerId is already present on the user object — no need to decode JWT
  const workerId = user?.workerId || null;
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef(null);

  // Derive unread count from notifications + current workerId
  const computeUnread = useCallback((data, wid) => {
    if (!wid) return data.length;
    return data.filter(n => !(n.read_by || []).includes(wid)).length;
  }, []);

  // Fetch latest notifications from the server
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await notificationsAPI.getAll({ limit: 50 });
      if (res.data?.success) {
        const data = res.data.notifications || [];
        setNotifications(data);
        setUnreadCount(computeUnread(data, workerId));
      }
    } catch (_) {
      // Silent fail — notification fetch must never break the app
    }
  }, [isAuthenticated, workerId, computeUnread]);

  // Mark a single notification as read
  const markRead = useCallback(async (id) => {
    try {
      await notificationsAPI.markRead(id);
      setNotifications(prev => {
        const updated = prev.map(n => {
          if (n.id !== id) return n;
          const readBy = n.read_by || [];
          return workerId && !readBy.includes(workerId)
            ? { ...n, read_by: [...readBy, workerId] }
            : n;
        });
        setUnreadCount(computeUnread(updated, workerId));
        return updated;
      });
    } catch (_) {}
  }, [workerId, computeUnread]);

  // Mark all notifications as read
  const markAllRead = useCallback(async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => {
        const updated = prev.map(n => {
          if (!workerId) return n;
          const readBy = n.read_by || [];
          return readBy.includes(workerId)
            ? n
            : { ...n, read_by: [...readBy, workerId] };
        });
        setUnreadCount(0);
        return updated;
      });
    } catch (_) {}
  }, [workerId]);

  // Start/stop polling based on auth state
  useEffect(() => {
    if (!isAuthenticated) {
      setNotifications([]);
      setUnreadCount(0);
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    // Initial fetch
    setLoading(true);
    fetchNotifications().finally(() => setLoading(false));

    // Poll every 15 seconds when the tab is visible
    intervalRef.current = setInterval(() => {
      if (!document.hidden) fetchNotifications();
    }, POLL_INTERVAL_MS);

    // Also re-fetch when the tab becomes visible again
    const handleVisibilityChange = () => {
      if (!document.hidden) fetchNotifications();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, fetchNotifications]);

  return (
    <NotificationContext.Provider
      value={{ notifications, unreadCount, loading, markRead, markAllRead, refresh: fetchNotifications }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used inside NotificationProvider');
  return ctx;
}
